import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  P360_FIRST_FYEAR,
  PLAN_REQUEST_FOR_PREV_YEARS,
} from 'legacy/lut/components/widgets/public/partner360/planning/config'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration/selectors/selectPartner360Data'
import { selectSectionSettings } from 'legacy/lut/features/partner360/sectionSettings'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useActualsTypes } from 'partner360/components/actualsTypeButtons'
import {
  type IJBPPlanning,
  type IPlanningData,
  useGetPlanningQuery,
} from 'partner360/entities/planning'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { applySobMandatorySettings } from '../lib/applySobMandatorySettings'
import { buildFinancialPlanQualityData } from '../lib/buildFinancialPlanQualityData'
import { filterApplicableCategories } from '../ui/financialPlanQuality/helpers/filterApplicableCategories'
import { getFinancialPlanQuality } from '../ui/financialPlanQuality/helpers/getFinancialPlanQuality'
import { hasFinancialPlan } from '../ui/financialPlanQuality/helpers/hasFinancialPlan'

import { IFinancialPlanQualityData } from './types'

const TOTAL_CATEGORY = 'jbp-total'
const FINANCIAL_PLAN_QUALITY_CURRENCY = 'USD'

/* A category scored across offerings: grouped under its offering, its ids kept apart from another
   offering's category of the same name. */
const withOffering = (
  row: IFinancialPlanQualityData,
  offering: string,
  offeringOrder: number
): IFinancialPlanQualityData => ({
  ...row,
  id: `${offering}\0${row.id}`,
  offering,
  offeringOrder,
  ...(row.children && {
    children: row.children.map((child) => ({ ...child, id: `${offering}\0${child.id}` })),
  }),
})

interface IUseFinancialPlanQualityParams {
  partnerId: string
  fYear: number
  /** `null` scores every offering the plan holds rows for, each category under its offering. */
  programType: ProgramTypes | null
  isGlobal: boolean
  /** The partner's own settings where the page stands on another partner; the page's otherwise. */
  partnerSettings?: {
    partnerFlags?: string
    isRegionalDistributor: boolean
    sectionSettings: ReturnType<typeof selectSectionSettings>
  }
  skip?: boolean
}

export const useFinancialPlanQuality = ({
  partnerId,
  fYear,
  programType,
  isGlobal,
  partnerSettings,
  skip = false,
}: IUseFinancialPlanQualityParams) => {
  const { partnerFlags: pagePartnerFlags = '' } = useSelector(selectPartner360Data)
  const partnerFlags = partnerSettings?.partnerFlags ?? pagePartnerFlags
  const pageSectionSettings = useSelector(selectSectionSettings)
  const { actualsTypes, actualsTypeDefault } = useActualsTypes()
  const { isRegionalDistributor: pageIsRegionalDistributor } = useSelector(selectPartnerAccess)
  const sectionSettings = partnerSettings?.sectionSettings ?? pageSectionSettings
  const actualsType = actualsTypes.includes(ActualsValueTypes.TNet)
    ? ActualsValueTypes.TNet
    : actualsTypeDefault
  const isRegionalDistributor = partnerSettings?.isRegionalDistributor ?? pageIsRegionalDistributor

  const { data, isFetching } = useGetPlanningQuery(
    {
      partnerId,
      fYear,
      fromFYear: Math.max(fYear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR),
      programType,
      isRegionalDistributor,
    },
    { skip: skip || programType === ProgramTypes.None }
  )

  const qualityFinData = useMemo(() => {
    const rows = data?.Planning ?? []
    const settings = data?.Settings ?? []
    const getApplicableFinData = (
      offering: string,
      offeringRows: IJBPPlanning[],
      offeringSettings: IPlanningData['Settings']
    ) =>
      filterApplicableCategories(
        buildFinancialPlanQualityData({
          rows: offeringRows,
          fYear,
          actualsType,
          settings: offeringSettings,
        }).filter(({ category }) => category !== TOTAL_CATEGORY),
        offeringSettings,
        offering,
        sectionSettings
      )
    // Across every offering: each one the plan holds rows for, on its own rows and settings
    const planOfferings = [...new Set(rows.map((row) => row.ProgramTypes?.trim()).filter(Boolean))]
    const categoryFinData =
      programType === null
        ? planOfferings.flatMap((offering, offeringOrder) =>
            getApplicableFinData(
              offering,
              rows.filter((row) => row.ProgramTypes?.trim() === offering),
              settings.filter((setting) => setting.ProgramType?.trim() === offering)
            ).map((row) => withOffering(row, offering, offeringOrder))
          )
        : getApplicableFinData(programType, rows, settings)

    return isGlobal
      ? categoryFinData.map((row) => ({ ...row, isFinancialApplicable: row.plan > 0 }))
      : categoryFinData
  }, [actualsType, data?.Planning, data?.Settings, fYear, isGlobal, programType, sectionSettings])

  const hasPlan = useMemo(() => hasFinancialPlan(qualityFinData), [qualityFinData])

  const quality = useMemo(
    () =>
      getFinancialPlanQuality(
        applySobMandatorySettings(
          qualityFinData,
          data?.Settings ?? [],
          { partnerFlags, sectionSettings },
          // Across every offering each category is matched by its own offering
          programType ?? undefined
        )
      ),
    [qualityFinData, data?.Settings, partnerFlags, sectionSettings, programType]
  )

  return {
    currency: FINANCIAL_PLAN_QUALITY_CURRENCY,
    hasPlan,
    isFetching,
    metric: actualsType,
    quality,
  }
}
